import { useEffect, useState, useSyncExternalStore } from "react";
import { ApiError, type ApiClient } from "../../api/client";
import type { DeviceLicence, DeviceStatus } from "../../api/types";
import { desktopApp } from "../../app/desktop";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { Dialog } from "../../ui/Dialog";
import { Logo } from "../../ui/Logo";
import { BancontactMark, BelfiusMark, KbcMark, MollieMark } from "../../ui/PaymentLogos";
import { QrCode } from "../../ui/QrCode";
import { dateTime, monthName } from "./subscription";

type Payment = NonNullable<DeviceLicence["payment"]>;

/** How often the till asks whether the payment at Mollie arrived, and for how long. */
const ASK_EVERY_MS = 4_000;
const ASK_FOR_MS = 20 * 60_000;

/** The month was paid: the licence as the server now gives it (null from a server that gives none). */
type OnPaid = (licence: DeviceLicence | null) => void;

interface Asked {
  checkoutUrl: string;
  /** Mollie's QR code of Bancontact; null: the till makes a QR of the payment page itself. */
  qrImage: string | null;
}

/**
 * Paying online at Mollie, shown at once (Furkan, 27/10/2026: "o knop ile acilsa, tam tersin, molie ekrani direct
 * ciksa"): Mollie's QR code of Bancontact to scan with the app of the bank, or Mollie's page to choose another way
 * ("andere betalingsmogelijkheden"); the till asks every few seconds whether the month was paid, and goes on by
 * itself once it is.
 */
function OnlinePay({ api, payment, onPaid }: { api: ApiClient; payment: Payment; onPaid: OnPaid }) {
  const text = t.subscription.pay;
  const [asked, setAsked] = useState<Asked | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [round, setRound] = useState(0);
  const [stopped, setStopped] = useState(false);
  const [choosing, setChoosing] = useState(false);

  useEffect(() => {
    let gone = false;
    api.request<Asked>("POST", "/device/licence/payment").then(
      (answer) => {
        if (!gone) setAsked(answer);
      },
      (error: unknown) => {
        if (!gone) setProblem(error instanceof ApiError ? error.message : t.errors.network);
      },
    );
    return () => {
      gone = true;
    };
  }, [api, round]);

  useEffect(() => {
    if (asked === null || stopped) return;
    const until = Date.now() + ASK_FOR_MS;
    const timer = window.setInterval(() => {
      if (Date.now() > until) {
        window.clearInterval(timer);
        // Mollie's payment has expired by now: a new QR code on a tap
        setStopped(true);
        return;
      }
      api.request<DeviceStatus>("GET", "/device").then(
        (status) => {
          // paid: nothing more to pay for this month
          if (status.licence?.payment?.period !== payment.period) {
            window.clearInterval(timer);
            onPaid(status.licence ?? null);
          }
        },
        () => {
          // no connection for a moment: ask again at the next turn
        },
      );
    }, ASK_EVERY_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [api, asked, stopped, payment.period, onPaid]);

  const again = () => {
    setAsked(null);
    setProblem(null);
    setStopped(false);
    setRound((count) => count + 1);
  };

  // Mollie's page to choose (Bancontact, KBC/CBC, Belfius): a payment of its own, in a window of its own. A browser
  // opens the window at the tap (a window opened later is blocked); the Tafo app for Windows opens the link in
  // Chrome or Edge itself
  const choose = async () => {
    if (choosing) return;
    setChoosing(true);
    setProblem(null);
    const desktop = desktopApp() !== null;
    const opened = desktop ? null : window.open("", "_blank");
    try {
      const { checkoutUrl } = await api.request<Asked>("POST", "/device/licence/payment", { body: { choose: true } });
      if (opened) {
        opened.opener = null;
        opened.location.href = checkoutUrl;
      } else if (desktop) window.open(checkoutUrl, "_blank");
    } catch (error) {
      opened?.close();
      setProblem(error instanceof ApiError ? error.message : t.errors.network);
    } finally {
      setChoosing(false);
    }
  };

  // Mollie gave no payment (no answer, or paying online is off): no QR to wait for, but why, a new try and whom
  // to ask, now that there is no other way to pay (29/10/2026)
  const failed = asked === null && problem !== null;
  return (
    <div className="licpay-online">
      {!failed && (
        <div className="licpay-scan">
          <div className="licpay-qr">
            {asked === null ? (
              <span className="licpay-qrwait" aria-hidden="true" />
            ) : asked.qrImage === null ? (
              <QrCode text={asked.checkoutUrl} label={text.onlineQr} />
            ) : (
              <img src={asked.qrImage} alt={text.onlineQr} />
            )}
          </div>
          <p className="licpay-scanhint">{asked === null ? text.onlineBusy : text.onlineScan}</p>
          <div className="licpay-methods">
            <BancontactMark />
            <KbcMark />
            <BelfiusMark />
          </div>
        </div>
      )}
      {problem !== null && (
        <p className="error-text" role="alert">
          {problem}
        </p>
      )}
      {failed ? (
        <>
          <button type="button" className="btn licpay-mollie" onClick={again}>
            {text.tryAgain}
          </button>
          <p className="note">{text.stuck}</p>
        </>
      ) : stopped ? (
        <button type="button" className="btn licpay-mollie" onClick={again}>
          {text.newQr}
        </button>
      ) : (
        <>
          <button
            type="button"
            className="btn licpay-mollie"
            disabled={choosing}
            onClick={() => {
              void choose();
            }}
          >
            {choosing ? text.otherBusy : text.other}
          </button>
          {asked !== null && (
            <p className="licpay-wait" role="status">
              <span className="licpay-pulse" aria-hidden="true" />
              {text.waiting}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * What to pay for the Tafo licence: at Mollie only, which counts at once, with Mollie's mark at the foot (Furkan,
 * 27/10/2026; 29/10/2026: "o zaman suanlik odemelerde sadece molie kalsin, ben otomatik yurusun istiyorum"). Never a
 * transfer, which Tafo does not see; without Tafo's key at Mollie the window only says whom to ask.
 */
function PaymentDetails({ payment, api, onPaid }: { payment: Payment; api: ApiClient; onPaid: OnPaid }) {
  const text = t.subscription.pay;
  const online = payment.online === true;
  return (
    <div className={online ? "licpay licpay-isonline" : "licpay"}>
      <div className="licpay-sum">
        <span>{text.forMonth(monthName(payment.period))}</span>
        <b>{formatEuro(payment.amountCents)}</b>
      </div>
      {online ? (
        <>
          <OnlinePay api={api} payment={payment} onPaid={onPaid} />
          <p className="licpay-via">
            {text.via} <MollieMark />
          </p>
        </>
      ) : (
        <p className="note">{text.onlineOff}</p>
      )}
    </div>
  );
}

/**
 * Paid (Furkan, 27/10/2026, Donass: "neden betaalbevestiging gelmedi, pencere kayboldu"): the till says so, with
 * how long the licence now runs and where the invoice goes, instead of only closing the window.
 */
function Paid({ licence }: { licence: DeviceLicence | null }) {
  const text = t.subscription.pay;
  const until = licence?.paidUntil;
  return (
    <div className="licpay licpay-paid" role="status">
      <span className="licpay-check" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
          <path
            d="M5 12.5l4.5 4.5L19 7.5"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <p className="licpay-paidtitle">{text.paidThanks}</p>
      {until !== undefined && <p>{text.paidUntil(`${String(Number(until.slice(8, 10)))} ${monthName(until)}`)}</p>}
      <p className="note">{text.paidInvoice}</p>
    </div>
  );
}

const SEEN = "tafo.licenceNotice";

/** Once per start of the till and per month to pay: closed, it stays closed until the till starts again. */
function seen(period: string): boolean {
  try {
    return window.sessionStorage.getItem(SEEN) === period;
  } catch {
    return false;
  }
}

function remember(period: string): void {
  try {
    window.sessionStorage.setItem(SEEN, period);
  } catch {
    // no storage: it shows again at the next start, which is what it is for
  }
}

/**
 * The window and the bar of the till (Furkan, 27/10/2026: "tekrar wegklikken yapsalar ... buraya tiklayinca yine
 * gelsin"): a tap on the bar opens the window again; once paid, the bar says nothing more.
 */
const listeners = new Set<() => void>();
let asking = 0;
let paidPeriod: string | null = null;

function notify() {
  for (const listener of listeners) listener();
}

function tell() {
  asking += 1;
  notify();
}

function listen(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** In the bar of the till, while a payment is asked: "Je licentie loopt af · Betalen", which opens the window. */
export function LicenceBarButton({ licence }: { licence: DeviceLicence | null | undefined }) {
  const paid = useSyncExternalStore(listen, () => paidPeriod);
  const payment = licence?.payment;
  if (!payment || paid === payment.period) return null;
  const text = t.subscription.pay;
  const days = Math.max(licence.daysLeft, 0);
  return (
    <button type="button" className="licbar" title={text.barHint(days)} onClick={tell}>
      <span className="licbar-dot" aria-hidden="true" />
      <span className="licbar-long">{text.bar}</span>
      <span className="licbar-short">{text.barShort}</span>
    </button>
  );
}

/** The licence ended unpaid: the till shows only this, until the payment is recorded ("Opnieuw controleren"). */
export function LicenceScreen({
  licence,
  shop,
  api,
  onRetry,
}: {
  licence: DeviceLicence;
  shop: string;
  api: ApiClient;
  onRetry: () => void;
}) {
  const text = t.subscription.pay;
  const [paid, setPaid] = useState<{ licence: DeviceLicence | null } | null>(null);
  if (paid !== null) {
    return (
      <main className="center-screen">
        <div className="card-panel licpay-panel">
          <Logo />
          <h1>{text.paidTitle}</h1>
          <Paid licence={paid.licence} />
          <button type="button" className="btn primary" onClick={onRetry}>
            {text.toTill}
          </button>
        </div>
      </main>
    );
  }
  return (
    <main className="center-screen">
      <div className="card-panel licpay-panel" role="alert">
        <Logo />
        <h1>{text.titleExpired}</h1>
        <p>
          <b>{shop}</b> · {text.blocked}
        </p>
        {licence.payment && (
          <PaymentDetails
            payment={licence.payment}
            api={api}
            onPaid={(now) => {
              setPaid({ licence: now });
            }}
          />
        )}
        <button type="button" className={licence.payment?.online === true ? "btn" : "btn primary"} onClick={onRetry}>
          {text.retry}
        </button>
      </div>
    </main>
  );
}

/** A few days before the end, or on a day's exception: at start-up the till asks for the payment. */
export function LicenceNotice({ licence, api }: { licence: DeviceLicence; api: ApiClient }) {
  const payment = licence.payment!;
  const [open, setOpen] = useState(() => !seen(payment.period));
  const opened = useSyncExternalStore(listen, () => asking);
  const [shownAt, setShownAt] = useState(opened);
  const [paid, setPaid] = useState<{ licence: DeviceLicence | null } | null>(null);
  // a tap on the bar of the till: the window again
  if (opened !== shownAt) {
    setShownAt(opened);
    setOpen(true);
  }
  if (!open) return null;
  const text = t.subscription.pay;
  const plan = t.subscription.plan;
  const close = () => {
    remember(payment.period);
    setOpen(false);
  };
  if (paid !== null) {
    return (
      <Dialog
        title={text.paidTitle}
        size="narrow"
        onClose={close}
        footer={
          <button type="button" className="btn primary" onClick={close}>
            {text.close}
          </button>
        }
      >
        <Paid licence={paid.licence} />
      </Dialog>
    );
  }
  const status =
    licence.state === "grace" && licence.graceUntil !== null
      ? plan.grace(dateTime(licence.graceUntil))
      : plan.due(Math.max(licence.daysLeft, 0), t.subscription.months[Number(payment.period.slice(5, 7)) - 1]!);
  return (
    <Dialog
      title={text.titleDue}
      subtitle={status}
      size="narrow"
      onClose={close}
      footer={
        <button type="button" className="btn" onClick={close}>
          {text.later}
        </button>
      }
    >
      <PaymentDetails
        payment={payment}
        api={api}
        onPaid={(now) => {
          // the bar of the till says nothing more; the window says it was paid
          paidPeriod = payment.period;
          remember(payment.period);
          setPaid({ licence: now });
          notify();
        }}
      />
    </Dialog>
  );
}
